import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import {
    CheckUserExists,
    ClearDownloadLink,
    CreateUser,
    DeleteUser,
    ExportUsers,
    LoadSelectedUser,
    LoadUser,
    LoadUsers,
    UpdateUser
} from './user-management.actions';
import { inject, Injectable } from '@angular/core';
import { IPermission, IUser, Page, SignedUrl } from '@monorepo/interfaces';
import { UserManagementService } from '../services/user-management.service';
import { finalize, Observable, tap } from 'rxjs';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';

export interface UserManagementStateModel {
    users: IUser[];
    user: IUser | undefined;
    selectedUser: IUser | undefined;
    totalItemCount: number;
    isLoading: boolean;
    isMeLoading: boolean;
    isExistingUser: boolean;
    isCheckingExistingUser: boolean;
    downloadLink?: string;
    isDownloadingUsers: boolean;
}

@State<UserManagementStateModel>({
    name: 'userManagement',
    defaults: {
        users: [],
        user: undefined,
        selectedUser: undefined,
        totalItemCount: 0,
        isLoading: false,
        isMeLoading: false,
        isExistingUser: false,
        isCheckingExistingUser: false,
        isDownloadingUsers: false
    }
})
@Injectable()
export class UserManagementState {
    private userService = inject(UserManagementService);

    @Selector()
    static user(state: UserManagementStateModel): IUser | undefined {
        return state.user;
    }

    @Selector()
    static users(state: UserManagementStateModel): IUser[] {
        return state.users;
    }

    @Selector()
    static selectedUser(state: UserManagementStateModel): IUser | undefined {
        return state.selectedUser;
    }

    @Selector()
    static totalItemCount(state: UserManagementStateModel): number {
        return state.totalItemCount;
    }

    @Selector()
    static isLoading(state: UserManagementStateModel): boolean {
        return state.isLoading;
    }

    @Selector()
    static isMeLoading(state: UserManagementStateModel): boolean {
        return state.isMeLoading;
    }

    @Selector()
    static userPermissions(state: UserManagementStateModel): IPermission[] {
        if (!state.user?.permissions) return [];

        return state.user.permissions;
    }

    @Selector()
    static isExistingUser(state: UserManagementStateModel): boolean {
        return state.isExistingUser;
    }

    @Selector()
    static isCheckingExistingUser(state: UserManagementStateModel): boolean {
        return state.isCheckingExistingUser;
    }

    @Selector()
    static downloadLink(state: UserManagementStateModel): string | undefined {
        return state.downloadLink;
    }

    @Selector()
    static isDownloadingUsers(state: UserManagementStateModel): boolean {
        return state.isDownloadingUsers;
    }

    @Action(LoadUser)
    loadUser(ctx: StateContext<UserManagementStateModel>): Observable<IUser | HttpErrorResponse> {
        ctx.patchState({ isMeLoading: true });
        return this.userService.getUser().pipe(
            tap((user: IUser) => ctx.patchState({ user })),
            finalize(() => ctx.patchState({ isMeLoading: false }))
        );
    }

    @Action(LoadSelectedUser)
    loadSelectedUser(
        ctx: StateContext<UserManagementStateModel>,
        action: LoadSelectedUser
    ): Observable<IUser | HttpErrorResponse> {
        this.setIsLoading(ctx, true);
        return this.userService.get(action.userId).pipe(
            tap((user: IUser) => ctx.patchState({ selectedUser: user })),
            finalize(() => this.setIsLoading(ctx, false))
        );
    }

    @Action(LoadUsers)
    loadUsers(ctx: StateContext<UserManagementStateModel>, action: LoadUsers): Observable<IUser[]> {
        this.setIsLoading(ctx, true);
        const payload = action.payload;
        let params = new HttpParams();
        if (payload.sort && payload.sort.column && payload.sort.direction) {
            params = params
                .append('orderBy', payload.sort.column)
                .append('sortOrder', payload.sort.direction.toUpperCase());
        }
        if (payload.searchTerm) {
            params = params.append('searchTerm', payload.searchTerm);
        }
        if (payload.roles && payload.roles.length > 0) {
            for (const role of payload.roles) {
                params = params.append('roles', role);
            }
        }
        if (payload.accountingUnitIds && payload.accountingUnitIds.length > 0) {
            for (const id of payload.accountingUnitIds) {
                params = params.append('accountingUnitIds', id);
            }
        }
        return this.userService.getAllPaginated(payload.page, payload.pageSize, params).pipe(
            map((users: Page<IUser>) => {
                ctx.patchState({
                    users: users.items,
                    totalItemCount: users.metadata.itemCount
                });
                return users.items;
            }),
            finalize(() => this.setIsLoading(ctx, false))
        );
    }

    @Action(CreateUser)
    createUser(ctx: StateContext<UserManagementStateModel>, action: CreateUser): Observable<IUser | HttpErrorResponse> {
        this.setIsLoading(ctx, true);
        return this.userService.create(action.user).pipe(finalize(() => this.setIsLoading(ctx, false)));
    }

    @Action(UpdateUser)
    updateUser(ctx: StateContext<UserManagementStateModel>, action: UpdateUser): Observable<IUser | HttpErrorResponse> {
        this.setIsLoading(ctx, true);
        return this.userService.update(action.user).pipe(
            tap((updatedUser: IUser) => {
                const state = ctx.getState();
                const updatedUsers = state.users.map(user => (user.id === action.userId ? updatedUser : user));
                ctx.patchState({
                    users: updatedUsers,
                    selectedUser: updatedUser
                });
            }),
            finalize(() => this.setIsLoading(ctx, false))
        );
    }

    @Action(DeleteUser)
    deleteUser(ctx: StateContext<UserManagementStateModel>, action: DeleteUser): Observable<void | HttpErrorResponse> {
        this.setIsLoading(ctx, true);
        return this.userService.delete(action.userId).pipe(
            tap(() => {
                const state = ctx.getState();
                const filteredUsers = state.users.filter(user => user.id !== action.userId);
                ctx.patchState({
                    users: filteredUsers,
                    selectedUser: undefined
                });
            }),
            finalize(() => this.setIsLoading(ctx, false))
        );
    }

    @Action(CheckUserExists)
    checkUserExists(
        ctx: StateContext<UserManagementStateModel>,
        action: CheckUserExists
    ): Observable<boolean | HttpErrorResponse> {
        ctx.patchState({ isCheckingExistingUser: true, isExistingUser: false });
        return this.userService.checkUserExists(action.oid).pipe(
            tap((exists: boolean) => {
                ctx.patchState({
                    isExistingUser: exists,
                    isCheckingExistingUser: false
                });
            }),
            finalize(() => ctx.patchState({ isCheckingExistingUser: false }))
        );
    }

    @Action(ExportUsers)
    exportUsers(ctx: StateContext<UserManagementStateModel>, action: ExportUsers): Observable<SignedUrl> {
        this.setIsDownloadingUsers(ctx, true);
        const payload = action.payload;
        let params = new HttpParams({ fromObject: { page: 0, pageSize: 0 } });
        if (payload.sort && payload.sort.column && payload.sort.direction) {
            params = params
                .append('orderBy', payload.sort.column)
                .append('sortOrder', payload.sort.direction.toUpperCase());
        }
        if (payload.searchTerm) {
            params = params.append('searchTerm', payload.searchTerm);
        }
        if (payload.roles && payload.roles.length > 0) {
            for (const role of payload.roles) {
                params = params.append('roles', role);
            }
        }
        if (payload.accountingUnitIds && payload.accountingUnitIds.length > 0) {
            for (const id of payload.accountingUnitIds) {
                params = params.append('accountingUnitIds', id);
            }
        }
        return this.userService.downloadUsers(params).pipe(
            tap(signedUrl => ctx.patchState({ downloadLink: signedUrl.signedURL })),
            finalize(() => this.setIsDownloadingUsers(ctx, false))
        );
    }

    @Action(ClearDownloadLink)
    clearDownloadLink(ctx: StateContext<UserManagementStateModel>): void {
        ctx.patchState({ downloadLink: undefined });
    }

    private setIsLoading(ctx: StateContext<UserManagementStateModel>, isLoading: boolean): void {
        ctx.patchState({ isLoading });
    }

    private setIsDownloadingUsers(ctx: StateContext<UserManagementStateModel>, isDownloading: boolean): void {
        ctx.patchState({ isDownloadingUsers: isDownloading });
    }
}
